import React, { useEffect, useState } from "react";
import {
  XCircle,
  Package,
  Calendar,
  User,
  FileText,
  Loader as LoaderIcon,
} from "lucide-react";
import axios from "axios";
import { domain } from "../../../../security";
import Loader from "../../../loader/Loader";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const POReceiptsModal = ({ isOpen, onClose, poId, poNumber }) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedReceipt, setExpandedReceipt] = useState(null);

  useEffect(() => {
    if (isOpen && poId) {
      fetchReceipts();
    }
  }, [isOpen, poId]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      // Use the new endpoint if available, otherwise fallback to all and filter
      const response = await axios.get(
        `${domain}/api/GoodsReceipts/byPo/${poId}`,
      );
      setReceipts(response.data);
    } catch (error) {
      console.error("Failed to fetch receipts", error);
      // Optional: fallback to getAll and filter client-side
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedReceipt(expandedReceipt === id ? null : id);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-xl">
              <Package size={18} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800">
                Goods Receipts for PO #{poNumber}
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                All receipts created against this purchase order
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition"
          >
            <XCircle size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : receipts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                <FileText size={22} className="text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium">No receipts found.</p>
              <p className="text-slate-400 text-sm mt-1">
                This PO has not been received yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {receipts.map((receipt) => (
                <div
                  key={receipt.id}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                >
                  {/* Receipt header (clickable) */}
                  <div
                    className="p-4 cursor-pointer hover:bg-slate-50 transition flex items-start justify-between"
                    onClick={() => toggleExpand(receipt.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-emerald-50 rounded-lg">
                        <Package size={14} className="text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800 text-sm">
                          {receipt.receiptNumber}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar size={10} />
                            {formatDate(receipt.receiptDate)}
                          </span>
                          <span className="flex items-center gap-1">
                            <User size={10} />
                            {receipt.receivedBy || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-slate-500">
                      {receipt.lines?.length || 0} item(s)
                    </div>
                  </div>

                  {/* Expanded lines */}
                  {expandedReceipt === receipt.id && (
                    <div className="border-t border-slate-100 bg-slate-50 p-4">
                      {receipt.notes && (
                        <div className="mb-3 p-2 bg-amber-50 rounded-lg text-xs text-amber-700">
                          <span className="font-bold">Notes:</span>{" "}
                          {receipt.notes}
                        </div>
                      )}
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        Received Items
                      </h4>
                      <div className="space-y-2">
                        {receipt.lines?.map((line) => (
                          <div
                            key={line.id}
                            className="bg-white p-3 rounded-lg border border-slate-100"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-slate-800 text-sm">
                                  {line.product?.productName ||
                                    "Unknown Product"}
                                </p>
                                <p className="text-xs text-slate-400">
                                  Qty Received: {line.quantityReceived}
                                </p>
                              </div>
                            </div>
                            {line.receiptSerialNumbers?.length > 0 && (
                              <div className="mt-2 text-xs">
                                <span className="font-medium text-slate-500">
                                  Serial Numbers:
                                </span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {line.receiptSerialNumbers.map((sn, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md"
                                    >
                                      {sn}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default POReceiptsModal;
